// TMOS Issue Service
// Date: November 15, 2025
// Purpose: Frontend service for issue tracking API

import api from './api';

export interface Issue {
  id: string;
  tenant_id?: string | null;
  created_by: string;
  title: string;
  description: string;
  issue_category_id?: string | null;
  category_name?: string | null;
  urgency: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  impact: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  privacy: 'PRIVATE' | 'PUBLIC';
  assigned_to?: string | null;
  internal_notes?: string | null;
  target_type: 'PLATFORM' | 'TENANT_ADMIN' | 'PROGRAM' | 'PROJECT' | 'USER' | 'TRANSFORMATION';
  target_id?: string | null;
  target_tenant_id?: string | null;
  escalated_from_issue_id?: string | null;
  escalated_to_user_id?: string | null;
  escalation_privacy?: 'PRIVATE' | 'PUBLIC' | null;
  discussion_id?: string | null;
  created_at: string;
  updated_at: string;
  resolved_at?: string | null;
  closed_at?: string | null;
  creator_name?: string;
  assigned_to_name?: string;
  target_name?: string;
}

export interface CreateIssueRequest {
  title?: string;
  description: string;
  issue_category_id?: string;
  urgency?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  impact?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  privacy?: 'PRIVATE' | 'PUBLIC';
  target_type: 'PLATFORM' | 'TENANT_ADMIN' | 'PROGRAM' | 'PROJECT' | 'USER' | 'TRANSFORMATION';
  target_id?: string;
  target_tenant_id?: string;
}

export interface UpdateIssueRequest {
  title?: string;
  description?: string;
  issue_category_id?: string | null;
  urgency?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  impact?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status?: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  privacy?: 'PRIVATE' | 'PUBLIC';
  assigned_to?: string | null;
  internal_notes?: string;
}

export interface IssueFilters {
  /** Single or comma-separated status (e.g. OPEN,RESOLVED) */
  status?: string;
  /** Single or comma-separated urgency (e.g. HIGH,CRITICAL) */
  urgency?: string;
  /** Single or comma-separated impact (e.g. HIGH,CRITICAL) */
  impact?: string;
  target_type?: string;
  issue_category_id?: string;
  issue_category_ids?: string[];
  search?: string;
  tenant_id?: string; // For admin only
}

class IssueService {
  /**
   * Get visible issues for current user
   */
  async getIssues(filters: IssueFilters = {}): Promise<Issue[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.append(key, value.join(','));
          }
          return;
        }
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/issues${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get issue by ID
   */
  async getIssueById(id: string): Promise<Issue> {
    const response = await api.get(`/issues/${id}`);
    return response.data.data;
  }

  /**
   * Create new issue
   */
  async createIssue(data: CreateIssueRequest): Promise<Issue> {
    const response = await api.post('/issues', data);
    return response.data.data;
  }

  /**
   * Update issue
   */
  async updateIssue(id: string, data: UpdateIssueRequest): Promise<Issue> {
    const response = await api.put(`/issues/${id}`, data);
    return response.data.data;
  }

  /**
   * Assign issue to user
   */
  async assignIssue(id: string, assignedToUserId: string): Promise<Issue> {
    const response = await api.post(`/issues/${id}/assign`, {
      assigned_to_user_id: assignedToUserId
    });
    return response.data.data;
  }

  /**
   * Escalate issue
   */
  async escalateIssue(
    id: string,
    escalatedToUserId: string,
    escalationPrivacy: 'PRIVATE' | 'PUBLIC',
    message?: string
  ): Promise<Issue> {
    const response = await api.post(`/issues/${id}/escalate`, {
      escalated_to_user_id: escalatedToUserId,
      escalation_privacy: escalationPrivacy,
      message
    });
    return response.data.data;
  }

  /**
   * Resolve issue
   */
  async resolveIssue(id: string): Promise<Issue> {
    const response = await api.post(`/issues/${id}/resolve`);
    return response.data.data;
  }

  /**
   * Reopen closed issue
   */
  async reopenIssue(id: string): Promise<Issue> {
    const response = await api.post(`/issues/${id}/reopen`);
    return response.data.data;
  }

  /**
   * Update issue status
   */
  async updateIssueStatus(id: string, status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED'): Promise<Issue> {
    const response = await api.put(`/issues/${id}`, { status });
    return response.data.data;
  }

  /**
   * Close issue
   */
  async closeIssue(id: string): Promise<Issue> {
    return this.updateIssueStatus(id, 'CLOSED');
  }

  /**
   * Delete issue (soft delete)
   */
  async deleteIssue(id: string): Promise<void> {
    await api.delete(`/issues/${id}`);
  }

  /**
   * Set issue to in progress
   */
  async setIssueInProgress(id: string): Promise<Issue> {
    return this.updateIssueStatus(id, 'IN_PROGRESS');
  }

  /**
   * Get all issues for platform admin
   */
  async getAllIssuesForAdmin(filters: IssueFilters = {}): Promise<Issue[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.append(key, value.join(','));
          }
          return;
        }
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/admin/issues${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get internal notes for issue (admin/owner only)
   */
  async getInternalNotes(id: string): Promise<{ internal_notes: string | null }> {
    const response = await api.get(`/admin/issues/${id}/internal-notes`);
    return response.data.data;
  }
}

export const issueService = new IssueService();


